/**
 * Utility for storing user preferences for column mapping confirmation modal
 */

const STORAGE_KEY_PREFIX = 'column-mapping-preference-';
const SESSION_STORAGE_KEY_PREFIX = 'column-mapping-preference-session-';

export type PreferenceScope = 'session' | 'permanent';

export interface ColumnMappingPreference {
  applyToAll: boolean;
  scope: PreferenceScope;
  savedAt: string;
}

/**
 * Get storage key for a specific column mapping preference
 */
const getStorageKey = (scope: PreferenceScope): string => {
  return scope === 'permanent'
    ? `${STORAGE_KEY_PREFIX}default`
    : `${SESSION_STORAGE_KEY_PREFIX}default`;
};

/**
 * Save user preference for column mapping
 */
export const saveColumnMappingPreference = (
  applyToAll: boolean,
  scope: PreferenceScope
): void => {
  try {
    const preference: ColumnMappingPreference = {
      applyToAll,
      scope,
      savedAt: new Date().toISOString(),
    };

    const storageKey = getStorageKey(scope);
    const storage =
      scope === 'permanent' ? localStorage : sessionStorage;

    storage.setItem(storageKey, JSON.stringify(preference));
  } catch (error) {
    console.error('Failed to save column mapping preference:', error);
  }
};

/**
 * Load user preference for column mapping
 * Checks permanent preference first, then session preference
 */
export const loadColumnMappingPreference = (): {
  applyToAll: boolean | null;
  scope: PreferenceScope | null;
} => {
  try {
    // Check permanent preference first
    const permanentKey = getStorageKey('permanent');
    const permanentPreference = localStorage.getItem(permanentKey);

    if (permanentPreference) {
      const parsed: ColumnMappingPreference = JSON.parse(permanentPreference);
      return {
        applyToAll: parsed.applyToAll,
        scope: 'permanent',
      };
    }

    // Check session preference
    const sessionKey = getStorageKey('session');
    const sessionPreference = sessionStorage.getItem(sessionKey);

    if (sessionPreference) {
      const parsed: ColumnMappingPreference = JSON.parse(sessionPreference);
      return {
        applyToAll: parsed.applyToAll,
        scope: 'session',
      };
    }

    return { applyToAll: null, scope: null };
  } catch (error) {
    console.error('Failed to load column mapping preference:', error);
    return { applyToAll: null, scope: null };
  }
};

/**
 * Clear saved preference
 */
export const clearColumnMappingPreference = (
  scope?: PreferenceScope
): void => {
  try {
    if (scope) {
      const storageKey = getStorageKey(scope);
      const storage = scope === 'permanent' ? localStorage : sessionStorage;
      storage.removeItem(storageKey);
    } else {
      // Clear both
      localStorage.removeItem(getStorageKey('permanent'));
      sessionStorage.removeItem(getStorageKey('session'));
    }
  } catch (error) {
    console.error('Failed to clear column mapping preference:', error);
  }
};






